import React, { type FC, useEffect, useMemo, useState } from 'react';
import { FinalDecisionMakingProperty } from '../../utility/types';
import {
  useContractOfferSetup, useCreateContractOffers, usePreviewContractOffer,
} from '../../hooks/api/ContractOffers/useContractOffers';
import {
  CreateOffersResult, defaultProfileId, financingLabel, formatOfferPrice, isValidPrice, offerPdfUrl, offersZipUrl, priceInputFromMaxBid, todayIso,
} from '../../utility/contractOffers';
import { getErrorMessages } from '../../utility/team';
import BuyCreditsModal from './BuyCreditsModal';

export type ContractReviewModalProps = {
  listSortId: string;
  rows: FinalDecisionMakingProperty[];
  onClose: () => void;
}

const inputClass = 'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6';
const primaryButton = 'rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-gray-400';
const secondaryButton = 'rounded-md px-3 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50';

// Review step of Automatic Contracting: one set of choices (who is buying, which profile, which form,
// which addenda) plus a price per property. Preview shows a contract without saving;
// Create contracts stores them (nothing is sent to anyone).
const ContractReviewModal: FC<ContractReviewModalProps> = ({ listSortId, rows, onClose }) => {
  const { setup, isLoading, error } = useContractOfferSetup();
  const preview = usePreviewContractOffer();
  const create = useCreateContractOffers();

  const [ buyerId, setBuyerId ] = useState('');
  const [ profileId, setProfileId ] = useState('');
  const [ templateId, setTemplateId ] = useState('');
  const [ addendumIds, setAddendumIds ] = useState<string[]>([]);
  const [ effectiveDate, setEffectiveDate ] = useState(todayIso());
  const [ prices, setPrices ] = useState<Record<string, string>>(() =>
    Object.fromEntries(rows.map((row) => [ String(row.propertyId), priceInputFromMaxBid(row.maxBidValue) ])));
  const [ previewingId, setPreviewingId ] = useState<string | null>(null);
  const [ messages, setMessages ] = useState<Record<string, string[]>>({});
  const [ result, setResult ] = useState<CreateOffersResult | null>(null);
  const [ createErrors, setCreateErrors ] = useState<string[]>([]);
  const [ buying, setBuying ] = useState(false);
  const [ creditsAfter, setCreditsAfter ] = useState<number | null>(null);
  const [ sign, setSign ] = useState(false);

  // Pick sensible starting choices once the options arrive.
  useEffect(() => {
    if (!setup) return;
    const firstBuyer = setup.buyers[0];
    setBuyerId((current) => current || firstBuyer?.id || '');
    setProfileId((current) => current || defaultProfileId(firstBuyer));
    setTemplateId((current) => current || setup.templates[0]?.id || '');
  }, [ setup ]);

  const buyer = setup?.buyers.find((b) => b.id === buyerId);
  const profile = buyer?.profiles.find((p) => p.id === profileId);
  const actingForOther = !!buyer && !buyer.self;
  const missing = profile?.missing ?? [];
  // A cash buyer has no loan, so a ticked financing addendum is left out of the contract.
  const financingSkipped = (profile?.financing_type ?? 'cash') === 'cash'
    && !!setup?.addenda.some((a) => a.addendum_tag === 'financing' && addendumIds.includes(a.id));
  const commissionBlocked = actingForOther && !setup?.commission_ready;
  const credits = creditsAfter ?? setup?.credits ?? 0;
  const signingAvailable = !!buyer?.signing?.available;
  // Signing only applies when it is switched on AND this buyer can actually be signed for.
  const signing = sign && signingAvailable;

  const problem = useMemo(() => {
    if (!setup) return null;
    if (setup.templates.length === 0) return 'No contract form is available yet.';
    if (!buyer) return 'No buyer is available.';
    if (buyer.profiles.length === 0) return 'This buyer has no Buyer Profile yet. Add one under Configuration > Buyer Profiles.';
    if (missing.length > 0) return `The Buyer Profile "${profile?.label}" is missing: ${missing.join(', ')}.`;
    return null;
  }, [ setup, buyer, profile, missing ]);

  const readyRows = rows.filter((row) => isValidPrice(prices[String(row.propertyId)] ?? ''));
  const outOfCredits = !!setup && credits < readyRows.length;

  const handleBuyerChange = (id: string) => {
    setBuyerId(id);
    setProfileId(defaultProfileId(setup?.buyers.find((b) => b.id === id)));
  };

  const toggleAddendum = (id: string) =>
    setAddendumIds((current) => (current.includes(id) ? current.filter((a) => a !== id) : [ ...current, id ]));

  const handlePreview = async (propertyId: string) => {
    setPreviewingId(propertyId);
    setMessages((current) => ({ ...current, [propertyId]: [] }));
    try {
      const response = await preview.mutateAsync({
        list_sort_id: listSortId, property_id: propertyId, buyer_id: buyerId, buyer_profile_id: profileId,
        template_id: templateId, addendum_ids: addendumIds, price: prices[propertyId] ?? '',
        effective_date: effectiveDate, sign: signing,
      });
      window.open(URL.createObjectURL(response.data as Blob), '_blank');
    } catch (err) {
      setMessages((current) => ({ ...current, [propertyId]: getErrorMessages(err) }));
    } finally {
      setPreviewingId(null);
    }
  };

  const handleCreate = async () => {
    setCreateErrors([]);
    setMessages({});
    try {
      const response = await create.mutateAsync({
        list_sort_id: listSortId, buyer_id: buyerId, buyer_profile_id: profileId, template_id: templateId,
        addendum_ids: addendumIds, effective_date: effectiveDate, sign: signing,
        items: readyRows.map((row) => ({ property_id: String(row.propertyId), price: prices[String(row.propertyId)] })),
      });
      setResult(response.data as CreateOffersResult);
      if (typeof response.data.credits === 'number') setCreditsAfter(response.data.credits);
    } catch (err) {
      const data = (err as { response?: { data?: CreateOffersResult } })?.response?.data;
      if (data && data.errors && !Array.isArray(data.errors)) {
        // Some properties failed: show what was stored and what went wrong per property
        setResult(data);
        setMessages(data.errors);
        if (typeof data.credits === 'number') setCreditsAfter(data.credits);
      } else {
        const left = (err as { response?: { data?: { credits?: number } } })?.response?.data?.credits;
        if (typeof left === 'number') setCreditsAfter(left);
        setCreateErrors(getErrorMessages(err));
      }
    }
  };

  const addressFor = (propertyId: string) => rows.find((row) => String(row.propertyId) === propertyId)?.address ?? propertyId;

  return (
    <div className='fixed inset-0 z-30 flex items-center justify-center bg-gray-900/50 p-4' data-tag='contract-review-modal'>
      <div role='dialog' aria-modal='true' aria-label='Review contracts' className='max-h-full w-full max-w-4xl overflow-y-auto rounded-lg bg-white p-6 text-sm shadow-xl'>
        <div className='flex items-start justify-between'>
          <div>
            <h2 className='text-lg font-semibold text-gray-900'>Review contracts ({rows.length})</h2>
            <p className='mt-1 text-gray-600'>Contracts are stored on REIX only. Nothing is sent to anyone.</p>
          </div>
          <button type='button' className={secondaryButton} onClick={onClose}>Close</button>
        </div>

        {isLoading && <p className='mt-6 text-gray-600'>Loading...</p>}
        {error && <p className='mt-6 text-red-600'>{getErrorMessages(error)[0]}</p>}

        {setup && (
          <>
            <div className='mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2'>
              <div>
                <label htmlFor='review-buyer' className='block font-medium text-gray-900'>Buyer</label>
                <select id='review-buyer' className={inputClass} value={buyerId} onChange={(e) => handleBuyerChange(e.target.value)}>
                  {setup.buyers.map((b) => <option key={b.id} value={b.id}>{b.self ? `${b.name} (me)` : b.name}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor='review-profile' className='block font-medium text-gray-900'>Buyer Profile</label>
                <select id='review-profile' className={inputClass} value={profileId} onChange={(e) => setProfileId(e.target.value)}>
                  {(buyer?.profiles ?? []).map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor='review-template' className='block font-medium text-gray-900'>Contract form</label>
                <select id='review-template' className={inputClass} value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                  {setup.templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor='review-effective-date' className='block font-medium text-gray-900'>Effective date</label>
                <input id='review-effective-date' type='date' className={inputClass} value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
              </div>
            </div>

            {setup.addenda.length > 0 && (
              <fieldset className='mt-4'>
                <legend className='font-medium text-gray-900'>Addenda to include</legend>
                <div className='mt-1 flex flex-wrap gap-x-6 gap-y-1'>
                  {setup.addenda.map((addendum) => (
                    <label key={addendum.id} className='flex items-center gap-x-2 text-gray-700'>
                      <input type='checkbox' checked={addendumIds.includes(addendum.id)} onChange={() => toggleAddendum(addendum.id)} />
                      {addendum.name}
                    </label>
                  ))}
                </div>
                {financingSkipped && (
                  <p role='alert' className='mt-2 rounded-md bg-amber-50 p-3 text-amber-800' data-tag='financing-skipped'>
                    This Buyer Profile is set to Cash, so the Third Party Financing Addendum is not needed and will be left out of the contract.
                    To include it, choose a loan type and fill in the loan terms under Configuration &gt; Buyer Profiles.
                  </p>
                )}
              </fieldset>
            )}

            <div className='mt-4' data-tag='signing-option'>
              <label className='flex items-center gap-x-2 text-gray-900'>
                <input
                  type='checkbox'
                  checked={signing}
                  disabled={!signingAvailable}
                  onChange={(e) => setSign(e.target.checked)}
                />
                {buyer?.self ? 'Sign with my saved signature and initials' : `Sign for ${buyer?.name ?? 'the buyer'} with their saved signature and initials`}
              </label>
              {!signingAvailable && buyer?.signing?.reason && <p className='ml-6 mt-1 text-gray-500'>{buyer.signing.reason}</p>}
              {signing && <p className='ml-6 mt-1 text-gray-500'>Initials go on each page and the signature on the signature page. Preview to check before creating.</p>}
            </div>

            <div className='mt-3 flex items-center justify-between'>
              <p className='text-gray-600'>Financing: {financingLabel(profile?.financing_type)}</p>
              <p className='text-gray-700' data-tag='credit-balance'>
                Credits: <span className='font-semibold'>{credits}</span>{' '}
                <button type='button' className='ml-1 text-indigo-600 underline' onClick={() => setBuying(true)}>Buy credits</button>
              </p>
            </div>
            {outOfCredits && !problem && (
              <p role='alert' className='mt-3 rounded-md bg-amber-50 p-3 text-amber-800'>
                Creating {readyRows.length} contract{readyRows.length === 1 ? '' : 's'} needs {readyRows.length} credit{readyRows.length === 1 ? '' : 's'}; you have {credits}. Each stored contract uses 1 credit; previews are free.
              </p>
            )}
            {problem && <p role='alert' className='mt-3 rounded-md bg-amber-50 p-3 text-amber-800'>{problem}</p>}
            {commissionBlocked && (
              <p className='mt-3 rounded-md bg-amber-50 p-3 text-amber-800'>
                You are acting for another buyer. Set up your broker-to-broker commission form under Configuration &gt; Contract Templates before creating contracts. Previews still work.
              </p>
            )}

            <table className='mt-6 w-full text-left'>
              <thead>
                <tr className='border-b border-gray-200 text-xs uppercase text-gray-500'>
                  <th className='py-2 pr-2'>Property</th>
                  <th className='py-2 pr-2'>Purchase price</th>
                  <th className='py-2' />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const id = String(row.propertyId);
                  const priceOk = isValidPrice(prices[id] ?? '');
                  return (
                    <tr key={id} className='border-b border-gray-100 align-top'>
                      <td className='py-3 pr-2 text-gray-900'>
                        {row.address}
                        {messages[id]?.length > 0 && (
                          <ul className='mt-1 text-xs text-red-600'>{messages[id].map((m) => <li key={m}>{m}</li>)}</ul>
                        )}
                      </td>
                      <td className='py-3 pr-2'>
                        <input
                          aria-label={`Purchase price for ${row.address}`}
                          className={inputClass}
                          inputMode='decimal'
                          value={prices[id] ?? ''}
                          onChange={(e) => setPrices((current) => ({ ...current, [id]: e.target.value }))}
                        />
                      </td>
                      <td className='py-3 text-right'>
                        <button
                          type='button'
                          className={secondaryButton}
                          disabled={!!problem || !priceOk || previewingId === id}
                          onClick={() => handlePreview(id)}
                        >
                          {previewingId === id ? 'Building...' : 'Preview contract'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {createErrors.length > 0 && (
              <ul role='alert' className='mt-3 text-sm text-red-600'>{createErrors.map((m) => <li key={m}>{m}</li>)}</ul>
            )}

            {result && result.results.length > 0 && (
              <div className='mt-4 rounded-md bg-green-50 p-3 text-green-800' data-tag='contract-create-result'>
                <p className='font-medium'>
                  {result.results.length} contract{result.results.length === 1 ? '' : 's'} stored.
                  {result.shared_with && ` This list was also shared with ${result.shared_with}, so they can see the offers.`}
                </p>
                <ul className='mt-1 list-disc pl-5'>
                  {result.results.map((offer) => (
                    <li key={offer.id}>
                      {addressFor(offer.property_id)} at {formatOfferPrice(offer.price)}{' '}
                      <a className='underline' href={offerPdfUrl(offer.id)} target='_blank' rel='noreferrer'>View PDF</a>
                    </li>
                  ))}
                </ul>
                <a
                  className={`${secondaryButton} mt-3 inline-block bg-white`}
                  data-tag='export-contracts-zip'
                  href={offersZipUrl(result.results.map((offer) => offer.id))}
                  download
                >
                  Export all as ZIP ({result.results.length})
                </a>
              </div>
            )}

            <div className='mt-6 flex items-center justify-end gap-x-3'>
              <button type='button' className={secondaryButton} onClick={onClose}>{result ? 'Done' : 'Cancel'}</button>
              <button
                type='button'
                data-tag='create-contracts-button'
                className={primaryButton}
                disabled={!!problem || commissionBlocked || outOfCredits || readyRows.length === 0 || create.isPending}
                onClick={handleCreate}
              >
                {create.isPending ? 'Creating...' : `Create contracts (${readyRows.length})`}
              </button>
            </div>
          </>
        )}
      </div>
      {buying && <BuyCreditsModal onClose={() => setBuying(false)} />}
    </div>
  );
};

export default ContractReviewModal;
